import { useContext, useState, useRef, useEffect } from "react";
import {
  Button,
  Center,
  FileInput,
  Stack,
  Text,
  Modal,
  Alert,
  ScrollArea,
  Group,
  Image,
  ActionIcon,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { Upload, Camera, AlertCircle, X, Refresh } from "tabler-icons-react";

import type { MutationTrigger } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import type { MutationDefinition } from "@reduxjs/toolkit/dist/query";
import type { Message } from "wall/types/message.model";

import MutationWrapper from "common/components/MutationWrapper";
import {
  useGetPresignedPostMutation,
  useUploadToS3Mutation,
} from "common/upload/api/upload.enpoints";
import { MessageTextAreaInput } from "wall/play/components/MessageTextAreaInput";
import { PseudoInput } from "wall/play/components/PseudoInput";
import { WallContext } from "wall/hooks/useWall";
import { compressImage } from "common/utils/imageCompression";
import {
  DEFAULT_PSG_BACKGROUND_MOBILE,
  DEFAULT_WHITE_COLOR,
} from "_graphic_design";
import { POST_MESSAGE_FORM_TEXTS } from "_constants";

interface PostMessageFormProps {
  addMessage: MutationTrigger<
    MutationDefinition<Partial<Message>, any, any, Message, any>
  >;
  addMessageResult: {
    data?: Message;
    isLoading: boolean;
    isError: boolean;
  };
  form: UseFormReturnType<
    Partial<Message>,
    (values: Partial<Message>) => Partial<Message>
  >;
  setIsMessageSent: React.Dispatch<React.SetStateAction<boolean>>;
}

export function PostMessageForm({
  addMessage,
  addMessageResult,
  form,
  setIsMessageSent,
}: PostMessageFormProps) {
  const wall = useContext(WallContext);
  const landingMode = wall?.landingMode;

  const isHybridType = wall?.type === "HYBRID";
  const isImagesType = wall?.type === "IMAGES";
  const isTextType = wall?.type === "TEXT";

  void isHybridType;

  // For TEXT type, never open camera automatically
  const openCameraFirst = isTextType
    ? false
    : landingMode === "photo"
      ? true
      : false;

  const [getPresignedPost, getPresignedPostResult] =
    useGetPresignedPostMutation();
  const [uploadToS3, uploadToS3Result] = useUploadToS3Mutation();

  const [file, setFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(openCameraFirst);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraPermission, setCameraPermission] = useState<
    "prompt" | "granted" | "denied"
  >("prompt");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Check camera permission status on component mount
  useEffect(() => {
    checkCameraPermission();
  }, []);

  // Handle camera opening based on permission status
  useEffect(() => {
    if (cameraOpen) {
      if (cameraPermission === "granted") {
        // Permission already granted, but check if stream needs initialization
        if (!cameraStream) {
          initializeCamera();
        } else if (videoRef.current && !videoRef.current.srcObject) {
          // Stream exists but video element lost reference
          videoRef.current.srcObject = cameraStream;
        }
      } else if (cameraPermission === "prompt") {
        // Need to request permission first
        requestCameraPermission();
      }
      // If permission is "denied", we'll show an error in the modal
    } else {
      stopCamera();
      setCameraError(null);
    }

    return () => {
      // Only stop camera when component unmounts or cameraOpen becomes false
      if (!cameraOpen) {
        stopCamera();
      }
    };
    // eslint-disable-next-line
  }, [cameraOpen, cameraPermission, cameraStream]);

  // Create image preview when file changes
  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setImagePreview(objectUrl);

      // Clean up the object URL when component unmounts or file changes
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else {
      setImagePreview(null);
    }
  }, [file]);

  const checkCameraPermission = async () => {
    try {
      // Check if we can query camera permissions
      if (navigator.permissions && navigator.permissions.query) {
        const permission = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        setCameraPermission(
          permission.state as "granted" | "denied" | "prompt"
        );

        permission.onchange = () => {
          setCameraPermission(
            permission.state as "granted" | "denied" | "prompt"
          );
        };
      }
    } catch (error) {
      // Fallback for browsers that don't support permissions API
      console.log("Permissions API not supported");
      // Assume we need to prompt for permission
      setCameraPermission("prompt");
    }
  };

  const requestCameraPermission = async () => {
    try {
      setIsRequestingPermission(true);
      setCameraError(null);

      // Check if browser supports media devices
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_BROWSER_NOT_SUPPORTED);
        setIsRequestingPermission(false);
        return;
      }

      // This will trigger the permission prompt
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      // Permission granted
      setCameraStream(stream);
      setCameraPermission("granted");

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Force video to play if it's paused
        if (videoRef.current.paused) {
          videoRef.current.play().catch(console.error);
        }
      }
    } catch (error: any) {
      console.error("Error accessing camera:", error);
      setCameraPermission("denied");

      // Handle specific error cases
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setCameraError(
          POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_DENIED_MESSAGE
        );
      } else if (
        error.name === "NotFoundError" ||
        error.name === "DevicesNotFoundError"
      ) {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_NO_CAMERA_FOUND);
      } else if (error.name === "NotSupportedError") {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_BROWSER_NOT_SUPPORTED);
      } else if (
        error.name === "NotReadableError" ||
        error.name === "TrackStartError"
      ) {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_ALREADY_IN_USE);
      } else {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_GENERIC_ERROR);
      }
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const initializeCamera = async () => {
    try {
      setCameraError(null);

      // Check if browser supports media devices
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_BROWSER_NOT_SUPPORTED);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      setCameraStream(stream);
      setCameraPermission("granted");

      // Ensure video element is properly connected
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Force video to play if it's paused
        if (videoRef.current.paused) {
          videoRef.current.play().catch(console.error);
        }
      }
    } catch (error: any) {
      console.error("Error accessing camera:", error);
      setCameraPermission("denied");

      // Handle specific error cases
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setCameraError(
          POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_DENIED_MESSAGE
        );
      } else if (
        error.name === "NotFoundError" ||
        error.name === "DevicesNotFoundError"
      ) {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_NO_CAMERA_FOUND);
      } else if (error.name === "NotSupportedError") {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_BROWSER_NOT_SUPPORTED);
      } else if (
        error.name === "NotReadableError" ||
        error.name === "TrackStartError"
      ) {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_ALREADY_IN_USE);
      } else {
        setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_GENERIC_ERROR);
      }
    }
  };

  const flipCamera = async () => {
    const newFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newFacingMode);

    // Stop current stream
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }

    // Reinitialize camera with new facing mode
    await initializeCamera();
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      context?.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob and create File object
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const capturedFile = new File([blob], `photo_${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            setFile(capturedFile);
            setCameraOpen(false);
            stopCamera();
          }
        },
        "image/jpeg",
        0.8
      );
    }
  };

  const handleOpenCamera = () => {
    if (cameraPermission === "denied") {
      setCameraError(POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_DENIED_MESSAGE);
      return;
    }

    setCameraOpen(true);

    // If permission is already granted but stream is not available, initialize camera
    if (cameraPermission === "granted" && !cameraStream) {
      initializeCamera();
    }
  };

  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
    // Close camera if open when user selects a file manually
    if (cameraOpen) {
      setCameraOpen(false);
      stopCamera();
    }
  };

  const removeImage = () => {
    setFile(null);
    setImagePreview(null);
  };

  const submit = async () => {
    let fileToUpload = file;

    // Prepare submission data based on wall type
    let submissionData = { ...form.values };

    // For IMAGES type, ensure no message is sent
    if (isImagesType) {
      submissionData.message = "";
    }

    // For TEXT type, ensure no image is sent
    if (isTextType) {
      submissionData.imageKey = undefined;
    }

    if (file && !isTextType) {
      try {
        setIsCompressing(true);
        // Compress image to WebP format before uploading
        fileToUpload = await compressImage(file, {
          maxSize: 1200,
          quality: 0.5,
          targetSizeMB: 1,
        });
      } catch (error) {
        console.warn("Image compression failed, using original file:", error);
        // Fallback to original file if compression fails
        fileToUpload = file;
      } finally {
        setIsCompressing(false);
      }

      try {
        console.log(
          "Getting presigned URL for file:",
          fileToUpload.name,
          fileToUpload.type,
          fileToUpload.size
        );
        const data = await getPresignedPost({
          fileName: fileToUpload.name,
          folder: "wall-" + wall.id,
        }).unwrap();
        console.log("Presigned URL obtained, uploading to S3");
        await uploadToS3({ file: fileToUpload, data });
        console.log(
          "S3 upload successful, adding message with imageKey:",
          data.fields.key
        );
        addMessage({ ...submissionData, imageKey: data.fields.key });
        setIsMessageSent(true);
      } catch (error) {
        console.error("Upload failed:", error);
        // The MutationWrapper will handle displaying the error message
        throw error; // Re-throw to let the form handle the error
      }
    } else {
      try {
        addMessage(submissionData);
        setIsMessageSent(true);
      } catch (error) {
        console.error("Message submission failed:", error);
        throw error;
      }
    }
  };

  return (
    <form className="w-full max-w-480" onSubmit={form.onSubmit(submit)}>
      <MutationWrapper
        message={POST_MESSAGE_FORM_TEXTS.UPLOAD_ERROR_MESSAGE}
        title={POST_MESSAGE_FORM_TEXTS.UPLOAD_ERROR_TITLE}
        variant="outline"
        result={addMessageResult}
      />
      <MutationWrapper
        message={POST_MESSAGE_FORM_TEXTS.UPLOAD_ERROR_MESSAGE}
        title={POST_MESSAGE_FORM_TEXTS.UPLOAD_ERROR_TITLE}
        variant="outline"
        result={getPresignedPostResult}
      />
      <MutationWrapper
        message={POST_MESSAGE_FORM_TEXTS.UPLOAD_ERROR_MESSAGE}
        title={POST_MESSAGE_FORM_TEXTS.UPLOAD_ERROR_TITLE}
        variant="outline"
        result={uploadToS3Result}
      />

      {/* Camera Modal - Updated to full screen with custom background */}
      <Modal
        opened={cameraOpen}
        onClose={() => setCameraOpen(false)}
        fullScreen
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        padding="md"
        styles={{
          root: {
            backgroundColor: "#131527",
            backgroundImage:
              wall?.images?.playBackground || DEFAULT_PSG_BACKGROUND_MOBILE,
            backgroundSize: "cover",
            backgroundPosition: "center",
          },
          modal: {
            backgroundColor: "transparent",
            color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
            border: "none",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          header: {
            backgroundColor: "transparent",
            color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
            borderBottom: "none",
            marginBottom: 0,
          },
          title: {
            color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
            fontSize: "1.5rem",
            fontWeight: "bold",
            textAlign: "center",
            width: "100%",
          },
          body: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "transparent",
          },
          close: {
            color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
        }}
        centered
      >
        <ScrollArea h="calc(100vh - 50px)" type="scroll">
          <Stack
            style={{ flex: 1, justifyContent: "space-between", height: "100%" }}
          >
            {isRequestingPermission ? (
              <div
                className="text-center py-8 mt-[20vh]"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ color: wall?.colors?.info || DEFAULT_WHITE_COLOR }}
                >
                  {POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_REQUESTING}
                </Text>
                <Text
                  size="sm"
                  style={{ color: wall?.colors?.info || DEFAULT_WHITE_COLOR }}
                  mt="sm"
                >
                  {POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_INSTRUCTION}
                </Text>
              </div>
            ) : cameraError ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                className="mt-[20vh]"
              >
                <Alert
                  icon={<AlertCircle size={16} />}
                  title={POST_MESSAGE_FORM_TEXTS.CAMERA_ERROR_TITLE}
                  color="red"
                  styles={{
                    root: {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      border: "none",
                    },
                    title: {
                      color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                    },
                    message: {
                      color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                    },
                  }}
                >
                  {cameraError}
                </Alert>
              </div>
            ) : cameraPermission === "denied" ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                className="mt-[20vh]"
              >
                <Alert
                  icon={<AlertCircle size={16} />}
                  title={POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_DENIED_TITLE}
                  color="red"
                  styles={{
                    root: {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      border: "none",
                    },
                    title: {
                      color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                    },
                    message: {
                      color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                    },
                  }}
                >
                  {POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_DENIED_MESSAGE}
                </Alert>
              </div>
            ) : !cameraStream ? (
              <div
                className="text-center py-8 mt-[20vh]"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ color: wall?.colors?.info || DEFAULT_WHITE_COLOR }}
                >
                  {POST_MESSAGE_FORM_TEXTS.CAMERA_LOADING}
                </Text>
              </div>
            ) : (
              <>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "70vh",
                      maxHeight: "600px",
                      margin: "0 auto",
                    }}
                  >
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <canvas ref={canvasRef} style={{ display: "none" }} />

                    {/* Action button positioned in bottom left */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "12px",
                        right: "12px",
                      }}
                    >
                      <ActionIcon
                        onClick={flipCamera}
                        variant="filled"
                        style={{
                          backgroundColor:
                            wall?.colors?.buttonBackground ||
                            "rgba(255, 255, 255, 0.2)",
                          color:
                            wall?.colors?.buttonText || DEFAULT_WHITE_COLOR,
                          border: `2px solid ${wall?.colors?.buttonBorder || "rgba(255, 255, 255, 0.5)"}`,
                          borderRadius: "50%",
                          width: "50px",
                          height: "50px",
                        }}
                        title={POST_MESSAGE_FORM_TEXTS.FLIP_CAMERA_BUTTON}
                      >
                        <Refresh size={24} />
                      </ActionIcon>
                    </div>
                  </div>
                </div>

                <div
                  className="text-center"
                  style={{ paddingBottom: "1.8rem" }}
                >
                  <Group position="center" spacing="md">
                    <Button
                      onClick={capturePhoto}
                      leftIcon={<Camera size={20} />}
                      size="lg"
                      style={{
                        borderWidth: "2px",
                        borderColor: wall?.colors?.buttonBorder,
                        color: wall?.colors?.buttonText,
                        backgroundColor: wall?.colors?.buttonBackground,
                      }}
                    >
                      {wall?.labels?.photoLabel ||
                        POST_MESSAGE_FORM_TEXTS.CAPTURE_PHOTO_BUTTON}
                    </Button>
                  </Group>
                </div>
              </>
            )}

            <div
              className="text-center"
              style={{ paddingBottom: "1rem", marginBottom: "100px" }}
            >
              <Button
                variant="outline"
                onClick={() => setCameraOpen(false)}
                style={{
                  color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                  borderColor: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                }}
                tabIndex={-1}
              >
                {cameraError || cameraPermission === "denied"
                  ? POST_MESSAGE_FORM_TEXTS.USE_FILE_SELECTOR_BUTTON
                  : wall.labels?.cancellationLabel ||
                    POST_MESSAGE_FORM_TEXTS.CANCEL_CAMERA_BUTTON}
              </Button>
            </div>

            {cameraPermission === "denied" && (
              <Alert
                color="yellow"
                styles={{
                  root: {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "none",
                  },
                  message: {
                    color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                  },
                }}
              >
                <Text size="sm">
                  {POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_HELP}
                </Text>
              </Alert>
            )}

            {cameraPermission === "prompt" &&
              !isRequestingPermission &&
              !cameraError && (
                <Alert
                  color="blue"
                  styles={{
                    root: {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      border: "none",
                    },
                    message: {
                      color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                    },
                  }}
                >
                  <Text size="sm">
                    {POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_PROMPT}
                  </Text>
                  <Button
                    onClick={requestCameraPermission}
                    color="blue"
                    variant="light"
                    fullWidth
                    mt="sm"
                    style={{
                      color: wall?.colors?.info || DEFAULT_WHITE_COLOR,
                    }}
                  >
                    {POST_MESSAGE_FORM_TEXTS.CAMERA_PERMISSION_BUTTON}
                  </Button>
                </Alert>
              )}
          </Stack>
        </ScrollArea>
      </Modal>

      {/* Rest of the form with image preview */}
      <Stack>
        <Center>
          <div dangerouslySetInnerHTML={{ __html: wall?.images?.formLogo! }} />
        </Center>

        <PseudoInput
          form={form}
          inputValueName="author"
          maxChar={20}
          minChar={1}
        />

        {!isImagesType && (
          <MessageTextAreaInput form={form} inputValueName="message" />
        )}
        {!isTextType && (
          <>
            {/* Image Preview */}
            {imagePreview && (
              <div style={{ position: "relative", marginTop: "1rem" }}>
                <Group position="center">
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <Image
                      src={imagePreview}
                      alt={POST_MESSAGE_FORM_TEXTS.IMAGE_PREVIEW_ALT}
                      width={200}
                      height={200}
                      fit="cover"
                      radius="sm"
                      style={{
                        border: `2px solid ${wall?.colors?.buttonBorder || "#ccc"}`,
                      }}
                    />
                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="filled"
                      style={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        borderRadius: "50%",
                      }}
                      className="bg-red-600"
                      onClick={removeImage}
                    >
                      <X size={14} />
                    </ActionIcon>
                  </div>
                </Group>
              </div>
            )}
            <div>
              <FileInput
                label={POST_MESSAGE_FORM_TEXTS.PHOTO_LABEL}
                styles={{
                  label: {
                    fontSize: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: wall?.colors?.labels,
                    span: "blue",
                  },
                  input: {
                    color: wall?.colors?.upload,
                    fontWeight: "normal",
                  },
                  placeholder: {
                    color: wall?.colors?.upload,
                    fontWeight: "normal",
                  },
                  required: { color: wall?.colors?.mandatory },
                }}
                radius={"xs"}
                icon={<Upload size={14} />}
                placeholder={
                  file
                    ? POST_MESSAGE_FORM_TEXTS.PHOTO_CAPTURED_PLACEHOLDER
                    : wall.labels?.addAPhotoLabel ||
                      POST_MESSAGE_FORM_TEXTS.ADD_PHOTO_PLACEHOLDER
                }
                className="hidden"
                required={wall && wall.type === "IMAGES"}
                value={file}
                accept="image/*"
                onChange={handleFileChange}
              />

              {/* Camera button for manual camera access */}
              <Text size={"xl"}>
                {wall.labels?.addAPhotoLabel ||
                  POST_MESSAGE_FORM_TEXTS.ADD_PHOTO_LABEL}
              </Text>
              <Button
                variant="outline"
                leftIcon={<Camera size={16} />}
                onClick={handleOpenCamera}
                size="sm"
                mt="xs"
                fullWidth
                disabled={cameraPermission === "denied"}
              >
                {cameraPermission === "denied"
                  ? POST_MESSAGE_FORM_TEXTS.CAMERA_ACCESS_BLOCKED
                  : wall.labels?.takeAPhotoLabel ||
                    POST_MESSAGE_FORM_TEXTS.TAKE_PHOTO_BUTTON}
              </Button>

              {cameraError && !cameraOpen && (
                <Alert icon={<AlertCircle size={16} />} color="red" mt="xs">
                  {cameraError}
                </Alert>
              )}
            </div>
          </>
        )}

        <div>
          <Text
            className={`text-center text-base my-1.5 mx-0 leading-5`}
            sx={{ color: wall?.colors?.info }}
          >
            {wall?.welcomeMsg}
          </Text>
        </div>
        <div className="text-center">
          <Button
            type="submit"
            className="mb-4"
            radius={"xs"}
            loading={
              isCompressing ||
              addMessageResult.isLoading ||
              getPresignedPostResult.isLoading ||
              uploadToS3Result.isLoading
            }
            styles={{
              root: {
                borderWidth: "2px",
                borderColor: wall?.colors?.buttonBorder,
                color: wall?.colors?.buttonText,
                padding: 0,
                width: "8rem",
              },
              inner: {
                backgroundColor: wall?.colors?.buttonBackground,
                fontSize: "1rem",
                textTransform: "uppercase",
                width: "100%",
              },
            }}
          >
            <span>
              {wall?.labels?.submitLabel ||
                POST_MESSAGE_FORM_TEXTS.SUBMIT_BUTTON}
            </span>
          </Button>
        </div>
      </Stack>
    </form>
  );
}
